import type { BackupFile } from './backupSerialize';
import { uploadBackupWithRetention, type DriveBackupWithRetentionResult, type RetentionResult } from './driveBackup';
import { DriveApiError } from './driveClient';

/**
 * ============================================================================
 * GOOGLE DRIVE — AUTOMATIC BACKUP (Stage 3C)
 * ============================================================================
 *
 * Runs at most ONE automatic Drive backup per 24 hours, from app
 * lifecycle events only (app open, and the moment Drive is connected and
 * authorized) — never on database writes, never on a timer, never in the
 * background. There is no service worker, no server-side job, no sync.
 *
 * HARD CONSTRAINTS THIS FILE HONOURS
 *  - The OAuth access token is held in memory only (see
 *    setDriveSessionToken) and is never persisted; a page reload loses it.
 *  - Tokens are never refreshed silently. With no token, automatic
 *    backup simply skips. Practical consequence: on a cold app open there
 *    is no token yet, so the app-open check always skips; the backup is
 *    actually taken when the user connects Google Drive in that session.
 *  - The payload is the existing backup format (BackupFile from
 *    createBackup) — no second format.
 *  - A Drive failure never touches local data: this file only READS a
 *    backup via the injected buildBackup(); it has no way to write to
 *    the local database.
 *  - "Last successful automatic backup" is recorded only AFTER the
 *    upload has been verified. Attempts and failures never advance it,
 *    and manual backups never touch it.
 *
 * Kept free of Dexie/React so the due-check, guard and timestamp logic
 * are directly unit-testable; the caller supplies buildBackup().
 */

export const AUTO_BACKUP_INTERVAL_MS = 24 * 60 * 60 * 1000;
export const LAST_AUTO_BACKUP_STORAGE_KEY = 'lic-hfl-loan-tracker:last-auto-drive-backup-at';

/** The tiny slice of the Web Storage API this file needs (so tests can pass a fake). */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** localStorage if the browser allows it (it can throw or be absent in locked-down modes), otherwise undefined. */
export function getBrowserStorage(): KeyValueStorage | undefined {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : undefined;
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Session token — in memory only
// ---------------------------------------------------------------------------

let sessionAccessToken: string | undefined;

/** Remember (or forget, with undefined) the current session's Drive access token. Memory only — never persisted. */
export function setDriveSessionToken(token: string | undefined): void {
  sessionAccessToken = token;
}

export function getDriveSessionToken(): string | undefined {
  return sessionAccessToken;
}

// ---------------------------------------------------------------------------
// Last successful automatic backup — the only thing persisted
// ---------------------------------------------------------------------------

/**
 * Returns the stored last-success time, or undefined if there is none or
 * it cannot be trusted (unreadable, unparseable, or dated in the future —
 * e.g. the device clock was changed; ignoring it errs on the side of
 * taking a backup rather than silently skipping for a long time).
 */
export function readLastAutoBackupAt(storage: KeyValueStorage | undefined, now: Date): string | undefined {
  if (!storage) return undefined;
  let raw: string | null;
  try {
    raw = storage.getItem(LAST_AUTO_BACKUP_STORAGE_KEY);
  } catch {
    return undefined;
  }
  if (!raw) return undefined;
  const time = Date.parse(raw);
  if (Number.isNaN(time) || time > now.getTime()) return undefined;
  return raw;
}

/** Records a successful automatic backup. Returns false (never throws) if storage refused the write. */
export function recordAutoBackupSuccess(storage: KeyValueStorage, at: Date): boolean {
  try {
    storage.setItem(LAST_AUTO_BACKUP_STORAGE_KEY, at.toISOString());
    return true;
  } catch {
    return false;
  }
}

/** Pure: is an automatic backup due? Exactly 24h after the last success counts as due. */
export function isAutoBackupDue(lastSuccessAtIso: string | undefined, now: Date): boolean {
  if (!lastSuccessAtIso) return true;
  const last = Date.parse(lastSuccessAtIso);
  if (Number.isNaN(last)) return true;
  return now.getTime() - last >= AUTO_BACKUP_INTERVAL_MS;
}

// ---------------------------------------------------------------------------
// The automatic backup run
// ---------------------------------------------------------------------------

export type AutoBackupResult =
  | { status: 'skipped_not_connected' }
  | { status: 'skipped_no_storage' }
  | { status: 'skipped_not_due'; lastSuccessAt: string }
  | { status: 'skipped_in_progress' }
  | { status: 'skipped_no_data' }
  | { status: 'success'; fileName: string; completedAt: string; retention: RetentionResult }
  | { status: 'failed'; reason: 'auth_expired' | 'error' };

export interface AutoBackupDeps {
  /** Builds the backup from the live local database (read-only). */
  buildBackup: () => Promise<BackupFile>;
  getAccessToken?: () => string | undefined;
  storage?: KeyValueStorage;
  now?: () => Date;
  upload?: (accessToken: string, backup: BackupFile) => Promise<DriveBackupWithRetentionResult>;
}

/**
 * In-memory single-flight guard: at most one automatic backup runs at a
 * time, however many code paths (StrictMode double effects, app open +
 * connect in quick succession) ask for one. It is set synchronously,
 * before the first await, so two calls made back-to-back cannot both
 * get past it.
 */
let autoBackupInFlight = false;

/**
 * Takes an automatic Drive backup if — and only if — one is due and it
 * is safe to. NEVER throws: every outcome is returned as a status, so a
 * caller (the app shell) can fire-and-forget it without any risk to the
 * local app.
 */
export async function runAutoBackupIfDue(deps: AutoBackupDeps): Promise<AutoBackupResult> {
  const now = deps.now ?? (() => new Date());

  const accessToken = (deps.getAccessToken ?? getDriveSessionToken)();
  if (!accessToken) return { status: 'skipped_not_connected' };

  // Without persistent storage we cannot honour "at most once per 24h",
  // so skip rather than risk uploading on every connect.
  const storage = deps.storage ?? getBrowserStorage();
  if (!storage) return { status: 'skipped_no_storage' };

  const lastSuccessAt = readLastAutoBackupAt(storage, now());
  if (lastSuccessAt !== undefined && !isAutoBackupDue(lastSuccessAt, now())) {
    return { status: 'skipped_not_due', lastSuccessAt };
  }

  if (autoBackupInFlight) return { status: 'skipped_in_progress' };
  autoBackupInFlight = true;

  try {
    const backup = await deps.buildBackup();
    // Never upload an empty snapshot: with retention, a few empty
    // automatic backups (e.g. after browser data was cleared) would
    // otherwise push the user's real backups out of the kept set.
    if (backup.loan === null) return { status: 'skipped_no_data' };

    const { upload, retention } = await (deps.upload ?? uploadBackupWithRetention)(accessToken, backup);

    // Only now — the upload has succeeded and been verified — advance the timer.
    const completedAt = now();
    recordAutoBackupSuccess(storage, completedAt);
    return { status: 'success', fileName: upload.fileName, completedAt: completedAt.toISOString(), retention };
  } catch (err) {
    return { status: 'failed', reason: err instanceof DriveApiError && err.status === 401 ? 'auth_expired' : 'error' };
  } finally {
    autoBackupInFlight = false;
  }
}
